// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A tooltip component for displaying detailed shipment information.
 * This module provides a rich tooltip that appears when hovering over
 * timeline segments, showing origin, destination, and event details
 * with their actual and expected dates. The tooltip is designed to
 * provide quick access to important shipment details without requiring
 * navigation to a separate view.
 *
 * Key features:
 * - Rich event detail display
 * - Origin/destination visualization
 * - Actual vs expected date comparison
 * - Fallback handling for missing data
 * - Responsive layout
 * - Consistent date formatting
 * - Accessibility support
 */
import { EventGroup } from '@/asset/api/types';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';

/**
 * Props for configuring the AssetShipmentTimelineTooltip component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * Event group data representing the shipment.
   * Contains origin, destination, and a sequence of events
   * with their actual and expected dates.
   *
   * Required fields:
   * - origin: Location where shipment started
   * - destination: Location where shipment ends
   * - events: Array of events in chronological order
   *
   * @example
   * ```ts
   * const shipment = {
   *   origin: { name: 'Warehouse A' },
   *   destination: { name: 'Customer Site' },
   *   events: [
   *     {
   *       id: '1',
   *       type: { name: 'Picked Up' },
   *       occurredAt: DateTime.now(),
   *       expectedAt: DateTime.now().minus({ days: 1 })
   *     }
   *   ]
   * };
   * ```
   */
  shipment: EventGroup;
}

/**
 * A tooltip component for displaying shipment details.
 * Renders a structured view of shipment information including
 * origin/destination locations and a chronological list of
 * events with their timing information.
 *
 * Features:
 * - Header showing origin and destination with directional arrow
 * - Chronological list of shipment events
 * - Display of both actual and expected dates
 * - Styled formatting for missing or incomplete data
 * - Responsive grid layout for event details
 * - Fallback labels for missing locations
 * - Consistent date/time formatting
 *
 * Visual Structure:
 * ```
 * ┌────────────────────────────────┐
 * │ Origin → Destination           │
 * ├────────────────────────────────┤
 * │ Event 1    Actual Date         │
 * │            (Expected Date)     │
 * │ Event 2    Actual Date         │
 * │            (Expected Date)     │
 * └────────────────────────────────┘
 * ```
 *
 * Date Handling:
 * - Actual dates shown with time when available
 * - Expected dates shown in parentheses
 * - Missing dates handled gracefully
 * - Dates formatted according to locale
 *
 * Usage example:
 * ```tsx
 * const shipment = {
 *   origin: { name: 'Warehouse A' },
 *   destination: { name: 'Customer Site' },
 *   events: [
 *     {
 *       id: '1',
 *       type: { name: 'Picked Up' },
 *       occurredAt: DateTime.now(),
 *       expectedAt: DateTime.now().minus({ days: 1 })
 *     }
 *   ]
 * };
 *
 * return (
 *   <Tooltip
 *     arrow
 *     title={
 *       <AssetShipmentTimelineTooltip
 *         shipment={shipment}
 *       />
 *     }
 *   >
 *     <TimelineSegment />
 *   </Tooltip>
 * );
 * ```
 *
 * @param props - Component configuration options
 */
export function AssetShipmentTimelineTooltip(props: Props) {
  // Use fallback labels if origin/destination are missing
  // This ensures the tooltip remains useful even with incomplete data
  const originLabel = props.shipment.origin
    ? getEffectiveLocationName(props.shipment.origin)
    : 'Origin';
  const destinationLabel = props.shipment.destination
    ? getEffectiveLocationName(props.shipment.destination)
    : 'Destination';

  return (
    <Stack minWidth={200}>
      <Typography
        gap={1}
        padding={1}
        display="flex"
        variant="subtitle2"
        alignItems="center"
      >
        <Typography variant="inherit">{originLabel}</Typography>
        <ArrowForwardRounded fontSize="inherit" />
        <Typography variant="inherit">{destinationLabel}</Typography>
      </Typography>
      <Divider />
      <Box
        display="grid"
        gridTemplateColumns="1fr auto"
        columnGap={2}
        padding={1}
      >
        {props.shipment.events.map((event) => {
          const expectedDateLabel =
            event.expectedAt === null
              ? 'no expected date'
              : `expected ${event.expectedAt.toLocaleString({ dateStyle: 'medium' })}`;

          const promiseDateLabel =
            event.promiseDate === null
              ? null
              : `promised ${event.promiseDate.toLocaleString({ dateStyle: 'medium' })}`;

          return (
            <Fragment key={event.id}>
              <Typography variant="body2">{event.type.name}</Typography>
              {event.occurredAt && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  textAlign="right"
                  gridColumn="2 / 2"
                >
                  {event.occurredAt.toLocaleString({
                    dateStyle: 'medium',
                    timeStyle: 'long',
                  })}
                </Typography>
              )}
              {promiseDateLabel && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  fontStyle="italic"
                  gridColumn="2 / 2"
                  sx={{
                    '&::before': { content: '"("' },
                    '&::after': { content: '")"' },
                    '&::before, &::after': {
                      display: event.occurredAt === null ? 'none' : 'inline',
                    },
                  }}
                >
                  {promiseDateLabel}
                </Typography>
              )}
              <Typography
                color="text.secondary"
                variant="body2"
                fontStyle="italic"
                gridColumn="2 / 2"
                sx={{
                  '&::before': { content: '"("' },
                  '&::after': { content: '")"' },
                  '&::before, &::after': {
                    display: event.occurredAt === null ? 'none' : 'inline',
                  },
                }}
              >
                {expectedDateLabel}
              </Typography>
            </Fragment>
          );
        })}
      </Box>
    </Stack>
  );
}
